'use client';

import { c as _c } from "react/compiler-runtime";
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
const tooltipContentVariants = tv({
  base: 'origin-(--transform-origin) transition-[transform,opacity] duration-100 ease-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-instant:transition-none data-ending-style:[transform:scale(0.98)] data-starting-style:[transform:scale(0.98)]',
  variants: {
    variant: {
      default: 'inline-flex w-fit max-w-xs items-center gap-1.5 rounded bg-primary px-2 py-1 font-base-copy text-white',
      card: 'flex w-max max-w-[calc(100vw-1.5rem)] flex-col items-start gap-1.5 rounded-lg border border-primary-green/5 bg-white p-3 font-body-small text-primary shadow-elevation3 sm:max-w-sm'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
type TooltipContentProps = TooltipPrimitive.Popup.Props & Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'> & VariantProps<typeof tooltipContentVariants>;
function TooltipProvider(t0) {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      delay: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const delay = t1 === undefined ? 0 : t1;
  let t2;
  if ($[3] !== delay || $[4] !== props) {
    t2 = <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />;
    $[3] = delay;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function Tooltip(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function TooltipTrigger(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function TooltipContent(t0) {
  const $ = _c(25);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      className,
      side: t1,
      sideOffset: t2,
      align: t3,
      alignOffset: t4,
      variant: t5,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  const side = t1 === undefined ? "top" : t1;
  const sideOffset = t2 === undefined ? 4 : t2;
  const align = t3 === undefined ? "center" : t3;
  const alignOffset = t4 === undefined ? 0 : t4;
  const variant = t5 === undefined ? "default" : t5;
  let t6;
  if ($[9] !== className || $[10] !== variant) {
    t6 = state => tooltipContentVariants({
      variant,
      className: typeof className === "function" ? className(state) : className
    });
    $[9] = className;
    $[10] = variant;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== variant) {
    t7 = variant === "default" ? <TooltipPrimitive.Arrow className="z-popup size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-primary fill-primary data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-start]:top-1/2! data-[side=left]:top-1/2! data-[side=right]:top-1/2! data-[side=inline-start]:-right-1 data-[side=left]:-right-1 data-[side=top]:-bottom-2.5 data-[side=inline-end]:-left-1 data-[side=right]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:-translate-y-1/2 data-[side=right]:-translate-y-1/2" /> : null;
    $[12] = variant;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== children || $[15] !== props || $[16] !== t6 || $[17] !== t7) {
    t8 = <TooltipPrimitive.Popup data-slot="tooltip-content" className={t6} {...props}>{children}{t7}</TooltipPrimitive.Popup>;
    $[14] = children;
    $[15] = props;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== align || $[20] !== alignOffset || $[21] !== side || $[22] !== sideOffset || $[23] !== t8) {
    t9 = <TooltipPrimitive.Portal><TooltipPrimitive.Positioner align={align} alignOffset={alignOffset} side={side} sideOffset={sideOffset} className="isolate z-popup">{t8}</TooltipPrimitive.Positioner></TooltipPrimitive.Portal>;
    $[19] = align;
    $[20] = alignOffset;
    $[21] = side;
    $[22] = sideOffset;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };